// File: src/pages/production/StartProduction.jsx
// PART 1/3 — imports, setup, state, helpers, and early effects

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Row,
  Input,
  Label,
  Button,
  Spinner,
  Table,
} from "reactstrap";
import swal from "sweetalert";
import _ from "lodash";
import { GrAddCircle } from "react-icons/gr";
import { FaEdit } from "react-icons/fa";

// 🧩 All project utilities & APIs must come from ../import/
import {
  _Get,
  _PostSave,
  _GetTargetData,
  _GetDataById,
  _GetDataBydatabaseName,
  StartProductionApi,
  GetUpdatedStock,
  GetProductionData,
  AllCategoryList,
  CreateAccountList,
} from "../import/ApiEndPoint/ApiCalling";

import { Create_Account_List } from "../import/ApiEndPoint/Api"; // keeping your alias

// ───────────────────────────────────────────────────────────────
// Component
// ───────────────────────────────────────────────────────────────

function StartProduction() {
  const navigate = useNavigate();

  // ── Loading/UI flags
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── Target / KPI panes
  const [targets, setTargets] = useState([]);
  const [apiData, setApiData] = useState(null); // final target product rollups
  const [apiSubData, setApiSubData] = useState(null); // sub-required product rollups
  const [allRequireTargets, setAllRequireTargets] = useState([]);
  const [selectedFinalProduct, setSelectedFinalProduct] = useState(null);

  // ── Live computed stats
  const [realTimeUpdate, setRealTimeUpdate] = useState(0);
  const [totalSqInCreated, setTotalSqInCreated] = useState(0);

  // ── Workers & per-row targets
  const [selectWorker, setSelectWorker] = useState([]);
  const [workersTarget, setWorkersTarget] = useState([]);

  // ── Product catalogs
  const [productName, setProductName] = useState([]); // step-filtered raw products
  const [totalProductName, setTotalProductName] = useState([]); // all products (final etc.)
  const [allProductName, setAllProductName] = useState([]); // full catalog for lookups
  const [filterWorkers, setFilterWorkers] = useState([]);

  // ── Step/process data
  const [stepsData, setStepsData] = useState(() => {
    try {
      const saved = localStorage.getItem("stepsData");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [processSteps, setProcessSteps] = useState([]);
  const [selectedStep, setSelectedStep] = useState(""); // display value
  const [selectedStepIndex, setSelectedStepIndex] = useState(() => {
    try {
      const saved = localStorage.getItem("selectedStepIndexSaved");
      return saved ? JSON.parse(saved) : 0;
    } catch {
      return 0;
    }
  });

  // ── Row/line items (dynamic)
  const [rowCount, setRowCount] = useState(() => {
    try {
      const saved = localStorage.getItem("rowsCount");
      return saved ? JSON.parse(saved) : 1;
    } catch {
      return 1;
    }
  });

  // finalProductCount: 2D – rows -> array of {name,count}
  const [finalProductCount, setFinalProductCount] = useState(() => {
    try {
      const saved = localStorage.getItem("finalProductCount");
      return saved ? JSON.parse(saved) : [[{ name: "Product 1", count: 1 }]];
    } catch {
      return [[{ name: "Product 1", count: 1 }]];
    }
  });

  // wastageProductCount: 2D – rows -> array
  const [wastageProductCount, setWastageProductCount] = useState(() => {
    try {
      const saved = localStorage.getItem("wastageProductCount");
      return saved ? JSON.parse(saved) : [[{ name: "wastage 1", count: 1 }]];
    } catch {
      return [[{ name: "wastage 1", count: 1 }]];
    }
  });

  // ── Selection state (per row)
  const [selectedWorkerName, setSelectedWorkerName] = useState(() => {
    try {
      const saved = localStorage.getItem("selectedWorkerName");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [selectedRawMaterial, setSelecteRawMaterial] = useState(() => {
    try {
      const saved = localStorage.getItem("selectedRawMaterial");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [selectedProductName, setSelectedProductName] = useState(() => {
    try {
      const saved = localStorage.getItem("selectedProductName");
      return saved ? JSON.parse(saved) : [[]];
    } catch {
      return [[]];
    }
  });
  const [selectedWastageName, setSelectedWastageName] = useState(() => {
    try {
      const saved = localStorage.getItem("selectedWastageName");
      return saved ? JSON.parse(saved) : [[]];
    } catch {
      return [[]];
    }
  });

  // ── Quantity & units hints
  const [productQuantity, setProductQuantity] = useState(() => {
    try {
      const saved = localStorage.getItem("productQuantitySaved");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [productUnit, setProductUnit] = useState(() => {
    try {
      const saved = localStorage.getItem("productUnitSaved");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // ── Submit blob (top-level)
  const [submitData, setSubmitData] = useState(() => {
    try {
      const saved = localStorage.getItem("submitData");
      return saved
        ? JSON.parse(saved)
        : {
            selectedProcessName: "",
            selectedProcess: "",
            selectedStepName: "",
            selectedStep: "",
            selectedWastageName: "",
            selectedWorker: "",
          };
    } catch {
      return {
        selectedProcessName: "",
        selectedProcess: "",
        selectedStepName: "",
        selectedStep: "",
        selectedWastageName: "",
        selectedWorker: "",
      };
    }
  });

  const [rowsData, setRowsData] = useState(() => {
    try {
      const saved = localStorage.getItem("rowsDataSaved");
      return saved
        ? JSON.parse(saved)
        : Array(rowCount).fill({
            user_name: "",
            rProduct_name: "",
            rProduct_name_Units: [],
            finalProductDetails: [],
            wastageProductDetails: [],
          });
    } catch {
      return Array(rowCount).fill({
        user_name: "",
        rProduct_name: "",
        rProduct_name_Units: [],
        finalProductDetails: [],
        wastageProductDetails: [],
      });
    }
  });

  const [productionData, setProductionData] = useState({
    user_name: "",
    user_id: "",
    database: "",
    step_name: "",
    totalStep: 0,
    currentStep: 0,
    product_details: Array(rowCount).fill({
      rProduct_name: "",
      rProduct_name_Units: [],
      finalProductDetails: [],
      wastageProductDetails: [],
    }),
  });

  // ───────────────────────────────────────────────────────────────
  // Compact UI helpers
  // ───────────────────────────────────────────────────────────────

  const clearfilled = () => {
    localStorage.removeItem("selectedWorkerName");
    localStorage.removeItem("stepsData");
    localStorage.removeItem("selectedRawMaterial");
    localStorage.removeItem("selectedProductName");
    localStorage.removeItem("selectedWastageName");
    localStorage.removeItem("submitData");
    localStorage.removeItem("rowsDataSaved");
    localStorage.removeItem("rowsCount");
    localStorage.removeItem("finalProductCount");
    localStorage.removeItem("wasteProductCount");
    localStorage.removeItem("productQuantitySaved");

    setStepsData([]);
    setSelectedWorkerName([]);
    setSelecteRawMaterial([]);
    setSelectedProductName([]);
    setSelectedWastageName([]);
    setSubmitData({});
    setRowsData([]);
    setRowCount([]);

    const finalProductSaved = [[{ name: "product 1", count: 1 }]];
    setFinalProductCount(finalProductSaved);

    const initialWastageProductCount = [[{ name: "wastage 1", count: 1 }]];
    setWastageProductCount(initialWastageProductCount);
    setProductQuantity([]);
    setProductUnit([]);

    localStorage.setItem(
      "wastageProductCount",
      JSON.stringify(initialWastageProductCount)
    );
  };

  // Keep localStorage in sync (compact + explicit)
  useEffect(() => {
    localStorage.setItem(
      "selectedStepIndexSaved",
      JSON.stringify(selectedStepIndex)
    );
  }, [selectedStepIndex]);

  useEffect(() => {
    localStorage.setItem("rowsCount", JSON.stringify(rowCount));
  }, [rowCount]);

  useEffect(() => {
    localStorage.setItem(
      "finalProductCount",
      JSON.stringify(finalProductCount)
    );
  }, [finalProductCount]);

  useEffect(() => {
    localStorage.setItem(
      "wastageProductCount",
      JSON.stringify(wastageProductCount)
    );
  }, [wastageProductCount]);

  useEffect(() => {
    localStorage.setItem(
      "productQuantitySaved",
      JSON.stringify(productQuantity)
    );
  }, [productQuantity]);

  useEffect(() => {
    localStorage.setItem("productUnitSaved", JSON.stringify(productUnit));
  }, [productUnit]);

  useEffect(() => {
    localStorage.setItem("stepsData", JSON.stringify(stepsData));
  }, [stepsData]);

  useEffect(() => {
    localStorage.setItem("submitData", JSON.stringify(submitData));
  }, [submitData]);

  useEffect(() => {
    localStorage.setItem(
      "selectedWorkerName",
      JSON.stringify(selectedWorkerName)
    );
  }, [selectedWorkerName]);

  useEffect(() => {
    localStorage.setItem(
      "selectedRawMaterial",
      JSON.stringify(selectedRawMaterial)
    );
  }, [selectedRawMaterial]);

  useEffect(() => {
    localStorage.setItem(
      "selectedProductName",
      JSON.stringify(selectedProductName)
    );
  }, [selectedProductName]);

  useEffect(() => {
    localStorage.setItem(
      "selectedWastageName",
      JSON.stringify(selectedWastageName)
    );
  }, [selectedWastageName]);

  // Initial “loading” shimmer
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(t);
  }, []);

  // Seed productionData.database from userData
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    if (userData) {
      setProductionData((prev) => ({
        ...prev,
        database: userData.database,
      }));
    }
  }, [stepsData]);

  // Load Process (Categories) once
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    if (!userData) return;

    AllCategoryList(userData?.id, userData?.database)
      .then((res) => setStepsData(res?.Category || []))
      .catch((err) => console.error("Error fetching steps data:", err));
  }, []);

  // Fetch Target list (flattened)
  const fetchTarget = async () => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    try {
      const response = await _GetTargetData(userData.database);
      const data =
        response.target?.flatMap((item) =>
          item.Target?.map((innerItem) => ({
            ...innerItem,
            mainOuterId: item._id,
            date: new Date(item.createdAt).toLocaleDateString(),
          }))
        ) || [];
      setTargets(data);
    } catch (e) {
      console.error("Error fetching product data:", e);
    }
  };

  useEffect(() => {
    fetchTarget();
  }, []);

  // ───────────────────────────────────────────────────────────────
  // Rollup helpers (final product targets)
  // ───────────────────────────────────────────────────────────────

  const categorizeData = (filterTargets, completedList) => {
    const product = filterTargets[0]?.product;
    const productId = filterTargets[0]?.productId;
    const unit = filterTargets[0]?.primaryUnit;
    const today = new Date();

    setAllRequireTargets(filterTargets[0].requiredProducts);

    const categorizedData = {
      daily: {
        product,
        productId,
        unit,
        dailyTargetPrimary: filterTargets[0]?.dailyTargetPrimary || 0,
        dailyTargetSecondary: filterTargets[0]?.dailyTargetSecondary || 0,
        AchievedTargetPrimary: 0,
        AchievedTargetSecondary: 0,
      },
      monthly: {
        product,
        productId,
        unit,
        dailyTargetPrimary: filterTargets[0]?.monthlyTargetPrimary || 0,
        dailyTargetSecondary: filterTargets[0]?.monthlyTargetSecondary || 0,
        AchievedTargetPrimary: 0,
        AchievedTargetSecondary: 0,
      },
      threeMonths: {
        product,
        productId,
        unit,
        dailyTargetPrimary: filterTargets[0]?.threeMonthsTargetPrimary || 0,
        dailyTargetSecondary: filterTargets[0]?.threeMonthsTargetSecondary || 0,
        AchievedTargetPrimary: 0,
        AchievedTargetSecondary: 0,
      },
      sixMonths: {
        product,
        productId,
        unit,
        dailyTargetPrimary: filterTargets[0]?.sixMonthsTargetPrimary || 0,
        dailyTargetSecondary: filterTargets[0]?.sixMonthsTargetSecondary || 0,
        AchievedTargetPrimary: 0,
        AchievedTargetSecondary: 0,
      },
      yearly: {
        product,
        productId,
        unit,
        dailyTargetPrimary: filterTargets[0]?.yearlyTargetPrimary || 0,
        dailyTargetSecondary: filterTargets[0]?.yearlyTargetSecondary || 0,
        AchievedTargetPrimary: 0,
        AchievedTargetSecondary: 0,
      },
    };

    const inRange = (entryDate, start, end) =>
      entryDate >= start && entryDate <= end;

    completedList.forEach((entry) => {
      const entryDate = new Date(entry.date);
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      const sixMonthsAgo = new Date(today);
      sixMonthsAgo.setMonth(today.getMonth() - 6);
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(today.getFullYear() - 1);

      const fUnits = entry.data?.fProduct_name_Units || [];
      const stockUnit = entry.data?.fProduct_name?.stockUnit;
      const secondaryUnit = entry.data?.fProduct_name?.secondaryUnit;

      const primaryUnitData = fUnits.find((u) => u.unit === stockUnit);
      const secondaryUnitData = fUnits.find((u) => u.unit === secondaryUnit);

      const isToday =
        entryDate.getFullYear() === today.getFullYear() &&
        entryDate.getMonth() === today.getMonth() &&
        entryDate.getDate() === today.getDate();

      const isThisMonth =
        entryDate.getFullYear() === today.getFullYear() &&
        entryDate.getMonth() === today.getMonth();

      if (isToday) {
        if (primaryUnitData)
          categorizedData.daily.AchievedTargetPrimary +=
            primaryUnitData.value || 0;
        if (secondaryUnitData)
          categorizedData.daily.AchievedTargetSecondary +=
            secondaryUnitData.value || 0;
      }

      if (isThisMonth) {
        if (primaryUnitData) {
          categorizedData.monthly.AchievedTargetPrimary +=
            primaryUnitData.value || 0;
          categorizedData.threeMonths.AchievedTargetPrimary +=
            primaryUnitData.value || 0;
          categorizedData.sixMonths.AchievedTargetPrimary +=
            primaryUnitData.value || 0;
          categorizedData.yearly.AchievedTargetPrimary +=
            primaryUnitData.value || 0;
        }
        if (secondaryUnitData) {
          categorizedData.monthly.AchievedTargetSecondary +=
            secondaryUnitData.value || 0;
          categorizedData.threeMonths.AchievedTargetSecondary +=
            secondaryUnitData.value || 0;
          categorizedData.sixMonths.AchievedTargetSecondary +=
            secondaryUnitData.value || 0;
          categorizedData.yearly.AchievedTargetSecondary +=
            secondaryUnitData.value || 0;
        }
      } else if (inRange(entryDate, threeMonthsAgo, today)) {
        if (primaryUnitData)
          categorizedData.threeMonths.AchievedTargetPrimary +=
            primaryUnitData.value || 0;
        if (secondaryUnitData)
          categorizedData.threeMonths.AchievedTargetSecondary +=
            secondaryUnitData.value || 0;
      } else if (inRange(entryDate, sixMonthsAgo, today)) {
        if (primaryUnitData)
          categorizedData.sixMonths.AchievedTargetPrimary +=
            primaryUnitData.value || 0;
        if (secondaryUnitData)
          categorizedData.sixMonths.AchievedTargetSecondary +=
            secondaryUnitData.value || 0;
      } else if (inRange(entryDate, oneYearAgo, today)) {
        if (primaryUnitData)
          categorizedData.yearly.AchievedTargetPrimary +=
            primaryUnitData.value || 0;
        if (secondaryUnitData)
          categorizedData.yearly.AchievedTargetSecondary +=
            secondaryUnitData.value || 0;
      }
    });

    return categorizedData;
  };

  // ───────────────────────────────────────────────────────────────
  // Rollup helpers (sub-required product targets)
  // ───────────────────────────────────────────────────────────────

  const categorizeDataUpdated = async (filterData, completedList) => {
    const product = filterData[0]?.rawMaterial;
    const productId = filterData[0]?._id;
    const unit = filterData[0]?.unit?.[0]?.unit;
    const dailyTarget = filterData[0]?.unit?.[0]?.dailyTarget || 0;
    const monthlyTarget = filterData[0]?.unit?.[0]?.monthlyTarget || 0;
    const threeMonthsTarget = filterData[0]?.unit?.[0]?.threeMonthsTarget || 0;
    const sixMonthsTarget = filterData[0]?.unit?.[0]?.sixMonthsTarget || 0;
    const yearlyTarget = filterData[0]?.unit?.[0]?.yearlyTarget || 0;

    const today = new Date();
    const out = {
      daily: { product, productId, unit, dailyTarget, AchievedTarget: 0 },
      monthly: { product, productId, unit, monthlyTarget, AchievedTarget: 0 },
      threeMonths: {
        product,
        productId,
        unit,
        threeMonthsTarget,
        AchievedTarget: 0,
      },
      sixMonths: {
        product,
        productId,
        unit,
        sixMonthsTarget,
        AchievedTarget: 0,
      },
      yearly: { product, productId, unit, yearlyTarget, AchievedTarget: 0 },
    };

    const inRange = (d, s, e) => d >= s && d <= e;

    completedList.forEach((entry) => {
      const entryDate = new Date(entry.date);
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      const sixMonthsAgo = new Date(today);
      sixMonthsAgo.setMonth(today.getMonth() - 6);
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(today.getFullYear() - 1);

      const fUnits = entry.data?.fProduct_name_Units || [];
      const stockUnit = entry.data?.fProduct_name?.stockUnit;

      const primary = fUnits.find((u) => u.unit === stockUnit);

      const isToday =
        entryDate.getFullYear() === today.getFullYear() &&
        entryDate.getMonth() === today.getMonth() &&
        entryDate.getDate() === today.getDate();

      const isThisMonth =
        entryDate.getFullYear() === today.getFullYear() &&
        entryDate.getMonth() === today.getMonth();

      if (isToday && primary) out.daily.AchievedTarget += primary.value || 0;
      if (isThisMonth && primary) {
        out.monthly.AchievedTarget += primary.value || 0;
        out.threeMonths.AchievedTarget += primary.value || 0;
        out.sixMonths.AchievedTarget += primary.value || 0;
        out.yearly.AchievedTarget += primary.value || 0;
      } else if (inRange(entryDate, threeMonthsAgo, today) && primary) {
        out.threeMonths.AchievedTarget += primary.value || 0;
      } else if (inRange(entryDate, sixMonthsAgo, today) && primary) {
        out.sixMonths.AchievedTarget += primary.value || 0;
      } else if (inRange(entryDate, oneYearAgo, today) && primary) {
        out.yearly.AchievedTarget += primary.value || 0;
      }
    });

    return out;
  };

  // ───────────────────────────────────────────────────────────────
  // Final product selector -> builds apiData
  // ───────────────────────────────────────────────────────────────

  const handleSelectFinalChange = async (selectedOption, id) => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    let completedList = [];

    setSelectedFinalProduct(selectedOption);

    const filterTargets = targets.filter((d) => d.product === selectedOption);
    if (!filterTargets.length) {
      console.warn("No matching target for selected product");
      return;
    }
    setAllRequireTargets(filterTargets[0].requiredProducts);

    const response = await GetProductionData(userData.database);
    if (response?.product) {
      response.product.forEach((item) => {
        item.product_details?.forEach((detail) => {
          detail.finalProductDetails?.forEach((product) => {
            if (product?.fProduct_name?._id === id) {
              completedList.push({ data: product, date: item.createdAt });
            }
          });
        });
      });
    }

    const newRollup = categorizeData(filterTargets, completedList);
    setApiData(newRollup);

    // reset volatile selections
    [
      "selectedWorkerName",
      "selectedRawMaterial",
      "selectedProductName",
      "selectedWastageName",
      "rowsDataSaved",
      "finalProductCount",
      "wasteProductCount",
      "productQuantitySaved",
    ].forEach((k) => localStorage.removeItem(k));

    setSelectedWorkerName([]);
    setSelecteRawMaterial([]);
    setSelectedProductName([]);
    setSelectedWastageName([]);
    setRowCount(1);
    setRowsData([]);
    setRealTimeUpdate(0);
    setWorkersTarget([]);
    setFinalProductCount([[{ name: "product 1", count: 1 }]]);
    setWastageProductCount([[{ name: "wastage 1", count: 1 }]]);
    setProductQuantity([]);
    setProductUnit([]);
  };

  // (PART 2/3 continues from here…)
  // PART 2/3 — sub-requirements, unit math, selectors, derived effects

  // Build sub-required product rollups for the selected step
  const SubRequiredProductsData = async (stepName) => {
    if (!selectedFinalProduct) return null;

    const step = stepName || submitData.selectedStepName;
    const filterData = allRequireTargets.filter((d) => d.step === step);
    const id = filterData?.[0]?.rawProductId;
    if (!id) return;

    const userData = JSON.parse(localStorage.getItem("userData"));
    const completedList = [];
    const response = await GetProductionData(userData.database);

    if (response?.product) {
      response.product.forEach((item) => {
        item.product_details?.forEach((detail) => {
          detail.finalProductDetails?.forEach((p) => {
            if (p?.fProduct_name?._id === id) {
              completedList.push({ data: p, date: item.createdAt });
            }
          });
        });
      });
    }

    const updated = await categorizeDataUpdated(filterData, completedList);
    setApiSubData(updated);
  };

  // Live “how many final units” from current rows
  useEffect(() => {
    const run = async () => {
      const product = apiSubData?.daily?.product;
      if (!product) return;

      const saved = localStorage.getItem("rowsDataSaved");
      const rows = saved ? JSON.parse(saved) : [];
      if (!rows.length) return;

      const totalFromUnits = (key) =>
        rows.reduce((acc, r) => {
          (r.finalProductDetails || []).forEach((fp) => {
            (fp.fProduct_name_Units || []).forEach((u) => {
              if (u.unit === key) acc += Number(u.qty || 0);
            });
          });
          return acc;
        }, 0);

      const totalSqIn = totalFromUnits("SqIn");
      const totalKg =
        rows.reduce((acc, r) => {
          (r.finalProductDetails || []).forEach((fp) => {
            (fp.fProduct_name_Units || []).forEach((u) => {
              if (u.unit === "Kg" || u.unit === "kg") acc += Number(u.qty || 0);
            });
          });
          return acc;
        }, 0) || 0;

      const totalProduced = totalSqIn || totalKg;

      const match = allProductName.find((i) => i.Product_Title === product);
      if (!match) return;

      const units = JSON.parse(JSON.stringify(match.Units || []));
      const stockUnit = match.stockUnit;

      // init conversions once
      if (units.length && !units.some((u) => u.conversionFactors)) {
        units.forEach((u) => {
          u.conversionFactors = units.map((o) => ({
            unit: o.unit,
            factor: u.qty && o.qty ? o.qty / u.qty : 0,
          }));
        });
      }

      // choose base unit SqIn, else Kg
      let base = units.find((u) => u.unit === "SqIn");
      if (!base) base = units.find((u) => u.unit === "Kg" || u.unit === "kg");
      if (!base) return;

      base.qty = totalProduced;
      base.value = totalProduced;

      units.forEach((u) => {
        if (u.unit === base.unit) return;
        const cf = base.conversionFactors?.find((f) => f.unit === u.unit);
        u.qty = cf ? Number((totalProduced * cf.factor).toFixed(3)) : 0;
        u.value = u.qty;
      });

      const stock = units.find((u) => u.unit === stockUnit)?.qty || 0;
      setRealTimeUpdate(stock);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsData, apiSubData, allProductName]);

  // ───────────────────────────────────────────────────────────────
  // Unit math (single place): raw, final, wastage
  // ───────────────────────────────────────────────────────────────

  const handleUnitValueChange = async (
    rowIndex,
    unitType, // "rProduct_name_Units" | "fProduct_name_Units" | "wProduct_name_Units"
    unitIndex,
    newValue,
    finalIndex, // for final/wastage arrays
    initialFlag = 1 // 0 when auto-prefill triggers guard rails
  ) => {
    const saved = JSON.parse(localStorage.getItem("rowsDataSaved") || "[]");
    const rows = saved.length ? saved : rowsData;
    const parsed = newValue !== "" ? parseFloat(newValue) : 0;

    // ---------- RAW MATERIAL UNITS ----------
    if (unitType === "rProduct_name_Units") {
      const selectedRaw = allProductName.find(
        (p) => p.id === selectedRawMaterial[rowIndex]
      );
      if (!selectedRaw) return;

      // Stock gate
      const stockRes = await GetUpdatedStock(
        selectedRaw?.warehouse?._id,
        selectedRaw?._id
      );
      const available = Number(stockRes?.currentStock?.currentStock || 0);

      if (parsed > available) {
        swal("Error", "Unavailable stock", "error");
        return;
      }

      const units = rows[rowIndex]?.rProduct_name_Units || [];
      // prepare conversions if missing
      if (units.length && !units.some((u) => u.conversionFactors)) {
        units.forEach((u) => {
          u.conversionFactors = units.map((o) => ({
            unit: o.unit,
            factor: u.qty && o.qty ? o.qty / u.qty : 0,
          }));
        });
      }

      const base = units[unitIndex];
      base.qty = parsed;
      base.value = parsed;

      // cascade others
      const cfs = base.conversionFactors || [];
      units.forEach((u) => {
        if (u.unit === base.unit) return;
        const cf = cfs.find((f) => f.unit === u.unit);
        u.qty = cf ? Number((parsed * cf.factor).toFixed(3)) : 0;
        u.value = u.qty;
      });

      rows[rowIndex] = {
        ...(rows[rowIndex] || {}),
        rProduct_name_Units: units,
        finalProductDetails: [],
        wastageProductDetails: [],
        status: "Pending",
      };

      // Reset final selections for this row
      const sp = [...selectedProductName];
      if (sp[rowIndex]) sp[rowIndex] = [];
      setSelectedProductName(sp);
      localStorage.setItem("selectedProductName", JSON.stringify(sp));

      const fpc = [...finalProductCount];
      fpc[rowIndex] = [{ name: "Product 1", count: 1 }];
      setFinalProductCount(fpc);
      localStorage.setItem("finalProductCount", JSON.stringify(fpc));

      const sw = [...selectedWastageName];
      sw[rowIndex] = [];
      setSelectedWastageName(sw);
      localStorage.setItem("selectedWastageName", JSON.stringify(sw));

      localStorage.setItem("rowsDataSaved", JSON.stringify(rows));
      setRowsData(rows);
      individuallyWorkerTarget(rowIndex);
      return;
    }

    // helpers for totals
    const totalBy = (arr, unit) =>
      (arr || []).reduce((acc, p) => {
        (p.fProduct_name_Units || []).forEach((u) => {
          if (
            unit === "SqIn"
              ? u.unit === "SqIn"
              : u.unit === "Kg" || u.unit === "kg"
          ) {
            acc += Number(u.qty || 0);
          }
        });
        return acc;
      }, 0);

    // ---------- FINAL PRODUCT UNITS ----------
    if (unitType === "fProduct_name_Units") {
      const row = rows[rowIndex] || {};
      const fpd =
        row.finalProductDetails?.[finalIndex]?.fProduct_name_Units || [];
      if (fpd.length && !fpd.some((u) => u.conversionFactors)) {
        fpd.forEach((u) => {
          u.conversionFactors = fpd.map((o) => ({
            unit: o.unit,
            factor: u.qty && o.qty ? o.qty / u.qty : 0,
          }));
        });
      }

      if (fpd[unitIndex]) {
        fpd[unitIndex].qty = parsed;
        fpd[unitIndex].value = parsed;
        const base = fpd[unitIndex];
        const cfs = base.conversionFactors || [];
        fpd.forEach((u) => {
          if (u.unit === base.unit) return;
          const cf = cfs.find((f) => f.unit === u.unit);
          u.qty = cf ? Number((parsed * cf.factor).toFixed(3)) : 0;
          u.value = u.qty;
        });
      }

      // Stock exceed guard: final <= raw
      const finalAll = row.finalProductDetails || [];
      finalAll[finalIndex] = {
        ...(finalAll[finalIndex] || {}),
        fProduct_name_Units: fpd,
      };

      const rawUnits = row.rProduct_name_Units || [];
      const rawSqIn = rawUnits.find((u) => u.unit === "SqIn")?.value || 0;
      const rawKg =
        rawUnits.find((u) => u.unit === "Kg" || u.unit === "kg")?.value || 0;

      const sumSq = totalBy(finalAll, "SqIn");
      const sumKg = totalBy(finalAll, "Kg");

      if (sumSq > rawSqIn || sumKg > rawKg) {
        swal("Error!", "Stock exceeding, lower the stock.", "error");
        if (initialFlag === 0) {
          // roll back auto-add product
          const fpc = [...finalProductCount];
          if (fpc[rowIndex]?.length > 1) fpc[rowIndex].pop();
          setFinalProductCount(fpc);
          localStorage.setItem("finalProductCount", JSON.stringify(fpc));

          const sp = [...selectedProductName];
          if (sp[rowIndex]?.length) sp[rowIndex].pop();
          setSelectedProductName(sp);
          localStorage.setItem("selectedProductName", JSON.stringify(sp));

          if (row.finalProductDetails?.length) row.finalProductDetails.pop();
        }
        localStorage.setItem("rowsDataSaved", JSON.stringify(rows));
        setRowsData(rows);
        return;
      }

      // derive wastage (first slot reserved as WASTAGE)
      const wastageSq = Number((rawSqIn - sumSq).toFixed(3));
      const wastageKg = Number((rawKg - sumKg).toFixed(3));

      const wastageUnits = [
        { unit: "SqIn", type: "text", qty: wastageSq, value: wastageSq },
        { unit: "Kg", type: "text", qty: wastageKg, value: wastageKg },
      ];

      const wMaster = allProductName.find((p) => p.id === "WASTAGE");
      const wId = wMaster?._id || row?.rProduct_name || null;
      row.wastageProductDetails = [
        { wProduct_name: wId, wProduct_name_Units: wastageUnits },
      ];

      rows[rowIndex] = {
        ...row,
        finalProductDetails: finalAll,
        status: "Completed",
      };

      const wn = [...selectedWastageName];
      wn[rowIndex] = ["WASTAGE"];
      setSelectedWastageName(wn);
      localStorage.setItem("selectedWastageName", JSON.stringify(wn));

      const wpc = [...wastageProductCount];
      wpc[rowIndex] = [{ name: "WASTAGE", count: 1 }];
      setWastageProductCount(wpc);
      localStorage.setItem("wastageProductCount", JSON.stringify(wpc));

      localStorage.setItem("rowsDataSaved", JSON.stringify(rows));
      setRowsData(rows);
      return;
    }

    // ---------- WASTAGE UNITS ----------
    if (unitType === "wProduct_name_Units") {
      const row = rows[rowIndex] || {};
      const wastages = JSON.parse(
        JSON.stringify(row.wastageProductDetails || [])
      );
      if (!wastages.length) return;

      const finalAll = row.finalProductDetails || [];
      const rawUnits = row.rProduct_name_Units || [];
      const rawSqIn = rawUnits.find((u) => u.unit === "SqIn")?.qty || 0;
      const rawKg =
        rawUnits.find((u) => u.unit === "Kg" || u.unit === "kg")?.qty || 0;

      const sumSq = totalBy(finalAll, "SqIn");
      const sumKg = totalBy(finalAll, "Kg");

      const totalWastageSq = rawSqIn - sumSq;
      const totalWastageKg = rawKg - sumKg;

      const added = wastages[finalIndex];
      if (!added) return;

      const units = added.wProduct_name_Units || [];
      if (units.length && !units.some((u) => u.conversionFactors)) {
        units.forEach((u) => {
          u.conversionFactors = units.map((o) => ({
            unit: o.unit,
            factor: u.qty && o.qty ? o.qty / u.qty : 0,
          }));
        });
      }

      if (units[unitIndex]) {
        units[unitIndex].qty = parsed;
        units[unitIndex].value = parsed;
        const base = units[unitIndex];
        const cfs = base.conversionFactors || [];
        units.forEach((u) => {
          if (u.unit === base.unit) return;
          const cf = cfs.find((f) => f.unit === u.unit);
          u.qty = cf ? Number((parsed * cf.factor).toFixed(3)) : 0;
          u.value = u.qty;
        });
      }

      // remaining go to first WASTAGE row
      const pure = wastages.slice(1);
      let usedSq = 0,
        usedKg = 0;
      pure.forEach((w) =>
        (w.wProduct_name_Units || []).forEach((u) => {
          if (u.unit === "SqIn") usedSq += Number(u.value || 0);
          if (u.unit === "Kg" || u.unit === "kg")
            usedKg += Number(u.value || 0);
        })
      );

      const remainSq = Number((totalWastageSq - usedSq).toFixed(3));
      const remainKg = Number((totalWastageKg - usedKg).toFixed(3));

      if (
        (remainSq < 0 || isNaN(remainSq)) &&
        (remainKg < 0 || isNaN(remainKg))
      ) {
        swal("Error!", "No Stock Available. Remove added wastage.", "error");
        if (initialFlag === 0) wastages.pop();
        row.wastageProductDetails = wastages;
        rows[rowIndex] = row;
        localStorage.setItem("rowsDataSaved", JSON.stringify(rows));
        setRowsData(rows);
        return;
      }

      if (wastages[0]?.wProduct_name_Units?.length) {
        wastages[0].wProduct_name_Units[0].qty = remainSq;
        wastages[0].wProduct_name_Units[0].value = remainSq;
        wastages[0].wProduct_name_Units[1].qty = remainKg;
        wastages[0].wProduct_name_Units[1].value = remainKg;
      }

      row.wastageProductDetails = wastages;
      rows[rowIndex] = row;
      localStorage.setItem("rowsDataSaved", JSON.stringify(rows));
      setRowsData(rows);
    }
  };

  // ───────────────────────────────────────────────────────────────
  // Worker / Raw / Product / Wastage selectors
  // ───────────────────────────────────────────────────────────────

  const handleWorkerChange = (index, _, id) => {
    const found = selectWorker.find((w) => w._id === id);
    if (!found) return;
    const next = [...rowsData];
    next[index] = { ...(next[index] || {}), user_name: found._id };
    setRowsData(next);
    localStorage.setItem("rowsDataSaved", JSON.stringify(next));
  };

  const handleWorkerChangeFuntion = (index, value, id) => {
    handleWorkerChange(index, value, id);
    const names = [...selectedWorkerName];
    names[index] = value;
    setSelectedWorkerName(names);

    const raws = [...selectedRawMaterial];
    raws[index] = "";
    setSelecteRawMaterial(raws);
  };

  const handleRawMaterialChange = async (index, value, id) => {
    const found = allProductName.find((p) => p.id === value);
    if (!found) {
      const next = [...rowsData];
      next[index] = {
        ...(next[index] || {}),
        rProduct_name: "",
        rProduct_name_Units: [],
      };
      setRowsData(next);

      const pq = [...productQuantity];
      pq[index] = { productName: "", productQuantity: 0 };
      setProductQuantity(pq);

      const pu = [...productUnit];
      pu[index] = 0;
      setProductUnit(pu);
      return;
    }

    // Set live stock hints
    GetUpdatedStock(found.warehouse._id, found._id).then((res) => {
      const stock = Number(res?.currentStock?.currentStock || 0);
      const pq = [...productQuantity];
      pq[index] = { productName: found?.Product_Title, productQuantity: stock };
      setProductQuantity(pq);

      const pu = [...productUnit];
      pu[index] = found.stockUnit;
      setProductUnit(pu);
    });

    const next = [...rowsData];
    next[index] = {
      ...(next[index] || {}),
      rProduct_name: id,
      rawMaterialName: found.id,
      rProduct_name_Units: (found.Units || []).map((u) => ({
        ...u,
        value: "",
      })),
      finalProductDetails: [],
      wastageProductDetails: [],
    };
    setRowsData(next);
    localStorage.setItem("rowsDataSaved", JSON.stringify(next));

    // reset finals & wastage selection visuals for this row
    const sp = [...selectedProductName];
    sp[index] = [];
    setSelectedProductName(sp);
    localStorage.setItem("selectedProductName", JSON.stringify(sp));

    const sw = [...selectedWastageName];
    sw[index] = [];
    setSelectedWastageName(sw);
    localStorage.setItem("selectedWastageName", JSON.stringify(sw));
  };

  const handleRawMaterialChangeFuntion = async (index, value, id) => {
    const found = allProductName.find((p) => p.id === value);
    if (found) {
      const stock = await GetUpdatedStock(found?.warehouse?._id, found?._id);
      if (Number(stock?.currentStock?.currentStock || 0) <= 0) {
        swal({
          title: "No Stock Available",
          text: `No stock available for ${found.id}`,
          icon: "warning",
          buttons: { cancel: "Cancel", confirm: `Add stock: ${found.id}` },
        }).then((go) => {
          if (go)
            navigate(`/app/freshlist/house/EditRawMateirals/${found?._id}`);
        });
        return;
      }
    }
    handleRawMaterialChange(index, value, id);
    setSelecteRawMaterial((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });

    // Reset finals for this row
    const sp = JSON.parse(JSON.stringify(selectedProductName));
    sp[index] = [];
    setSelectedProductName(sp);
    localStorage.setItem("selectedProductName", JSON.stringify(sp));

    const fpc = [...finalProductCount];
    fpc[index] = [{ name: "Product 1", count: 1 }];
    setFinalProductCount(fpc);
    localStorage.setItem("finalProductCount", JSON.stringify(fpc));

    const sw = [...selectedWastageName];
    sw[index] = [];
    setSelectedWastageName(sw);
    localStorage.setItem("selectedWastageName", JSON.stringify(sw));
  };

  const handleProductNameChange = (index, value, id, finalIndex) => {
    const found = allProductName.find((p) => p.id === value);
    const next = JSON.parse(JSON.stringify(rowsData));

    if (found) {
      next[index].finalProductDetails[finalIndex] = {
        ...(next[index].finalProductDetails?.[finalIndex] || {}),
        fProduct_name: id,
        fProduct_name_Units: (found.Units || []).map((u) => ({
          ...u,
          value: "",
        })),
      };
    } else {
      next[index].finalProductDetails[finalIndex] = {
        fProduct_name: "",
        fProduct_name_Units: [],
      };
    }

    setRowsData(next);
    localStorage.setItem("rowsDataSaved", JSON.stringify(next));

    if (found) {
      const prefill = found.Units?.[0]?.qty ?? 0;
      handleUnitValueChange(
        index,
        "fProduct_name_Units",
        0,
        prefill,
        finalIndex,
        0
      );
    }
  };

  const handleProductNameChangeFuntion = (index, value, id, finalIndex) => {
    localStorage.removeItem("rowsDataSaved");
    handleProductNameChange(index, value, id, finalIndex);

    const next = [...selectedProductName];
    if (!next[index]) next[index] = [];
    while (next[index].length <= finalIndex) next[index].push("");
    next[index][finalIndex] = value;
    setSelectedProductName(next);
  };

  const handleWastageChange = async (index, _value, id, finalIndex) => {
    const found = allProductName.find((p) => p._id === id);
    const next = _.cloneDeep(rowsData);

    // Default “WASTAGE”
    if (found?.id === "WASTAGE") {
      const finalAll = next[index]?.finalProductDetails || [];
      const rawUnits = next[index]?.rProduct_name_Units || [];
      const rawSqIn = rawUnits.find((u) => u.unit === "SqIn")?.qty || 0;
      const rawKg =
        rawUnits.find((u) => u.unit === "Kg" || u.unit === "kg")?.qty || 0;

      const sumBy = (unit) =>
        finalAll.reduce((acc, p) => {
          (p.fProduct_name_Units || []).forEach((u) => {
            if (
              unit === "SqIn"
                ? u.unit === "SqIn"
                : u.unit === "Kg" || u.unit === "kg"
            ) {
              acc += Number(u.qty || 0);
            }
          });
          return acc;
        }, 0);

      const remainSq = Number((rawSqIn - sumBy("SqIn")).toFixed(3));
      const remainKg = Number((rawKg - sumBy("Kg")).toFixed(3));

      const wMaster = allProductName.find((p) => p.id === "WASTAGE");
      const wId = wMaster?._id || next[index]?.rProduct_name || null;

      next[index].wastageProductDetails = [
        {
          wProduct_name: wId,
          wProduct_name_Units: [
            { unit: "SqIn", type: "text", qty: remainSq, value: remainSq },
            { unit: "Kg", type: "text", qty: remainKg, value: remainKg },
          ],
        },
      ];

      setWastageProductCount((prev) => {
        const w = [...prev];
        w[index] = [{ name: "WASTAGE", count: 1 }];
        localStorage.setItem("wastageProductCount", JSON.stringify(w));
        return w;
      });

      const sn = [...selectedWastageName];
      sn[index] = ["WASTAGE"];
      setSelectedWastageName(sn);
      localStorage.setItem("selectedWastageName", JSON.stringify(sn));

      setRowsData(next);
      localStorage.setItem("rowsDataSaved", JSON.stringify(next));
      return;
    }

    // Custom wastage product
    if (found) {
      next[index].wastageProductDetails[finalIndex] = {
        ...(next[index].wastageProductDetails?.[finalIndex] || {}),
        wProduct_name: id,
        wProduct_name_Units: (found.Units || []).map((u) => ({ ...u })),
      };
    } else {
      next[index].wastageProductDetails[finalIndex] = {
        ...(next[index].wastageProductDetails?.[finalIndex] || {}),
        wProduct_name: "",
        wProduct_name_Units: [],
      };
    }

    setRowsData(next);
    localStorage.setItem("rowsDataSaved", JSON.stringify(next));

    if (found) {
      const prefill = found.Units?.[0]?.qty ?? 0;
      handleUnitValueChange(
        index,
        "wProduct_name_Units",
        0,
        prefill,
        finalIndex,
        0
      );
    }
  };

  const handleWastageChangeFuntion = (index, value, id, finalIndex) => {
    handleWastageChange(index, value, id, finalIndex);

    const next = [...selectedWastageName];
    if (!next[index]) next[index] = [];
    while (next[index].length <= finalIndex) next[index].push("");
    next[index][finalIndex] = value;
    setSelectedWastageName(next);
  };

  // ───────────────────────────────────────────────────────────────
  // Row & product add/remove
  // ───────────────────────────────────────────────────────────────

  const handleIncreaseRow = () => {
    setRowCount((c) => c + 1);
    const next = [
      ...rowsData,
      {
        user_name: "",
        rProduct_name: "",
        finalProductDetails: [],
        wastageProductDetails: [],
        rProduct_name_Units: [],
      },
    ];
    localStorage.setItem("rowsDataSaved", JSON.stringify(next));
    setRowsData(next);

    setFinalProductCount((prev) => {
      const out = [...prev, [{ name: "Product 1", count: 1 }]];
      localStorage.setItem("finalProductCount", JSON.stringify(out));
      return out;
    });
  };

  const handleRemoveRow = (rowIndex) => {
    if (rowsData.length <= 1) return;
    const nextRows = [...rowsData];
    nextRows.splice(rowIndex, 1);

    const nextF = [...finalProductCount];
    nextF.splice(rowIndex, 1);

    const pq = [...productQuantity];
    const pu = [...productUnit];
    if (rowIndex < pq.length) pq.splice(rowIndex, 1);
    if (rowIndex < pu.length) pu.splice(rowIndex, 1);

    const newCount = rowCount - 1;

    const sp = [...selectedProductName];
    if (sp[rowIndex]) sp[rowIndex] = [];
    setSelectedProductName(sp);
    localStorage.setItem("selectedProductName", JSON.stringify(sp));

    const sn = [...selectedWorkerName];
    if (sn[rowIndex] !== undefined) sn.pop();
    setSelectedWorkerName(sn);
    localStorage.setItem("selectedWorkerName", JSON.stringify(sn));

    const sr = [...selectedRawMaterial];
    if (sr[rowIndex] !== undefined) sr.pop();
    setSelecteRawMaterial(sr);
    localStorage.setItem("selectedRawMaterial", JSON.stringify(sr));

    setRowsData(nextRows);
    setFinalProductCount(nextF);
    setProductQuantity(pq);
    setProductUnit(pu);
    setRowCount(newCount);

    localStorage.setItem("rowsDataSaved", JSON.stringify(nextRows));
    localStorage.setItem("finalProductCount", JSON.stringify(nextF));
    localStorage.setItem("productQuantitySaved", JSON.stringify(pq));
    localStorage.setItem("rowsCount", JSON.stringify(newCount));
  };

  const handleAddMore = (rowIndex) => {
    const fpc = [...finalProductCount];
    if (!fpc[rowIndex]) fpc[rowIndex] = [{ name: "Product 1", count: 1 }];
    else {
      const nextIdx = fpc[rowIndex].length + 1;
      fpc[rowIndex].push({ name: `Product ${nextIdx}`, count: 1 });
    }
    setFinalProductCount(fpc);
    localStorage.setItem("finalProductCount", JSON.stringify(fpc));
  };

  const handleRemove = (rowIndex, itemIndex) => {
    const fpc = [...finalProductCount];
    const sp = [...selectedProductName];
    const rows = JSON.parse(JSON.stringify(rowsData));

    if (sp[rowIndex]) sp[rowIndex].splice(itemIndex, 1);
    setSelectedProductName(sp);
    localStorage.setItem("selectedProductName", JSON.stringify(sp));

    if (fpc[rowIndex]?.[itemIndex]) {
      fpc[rowIndex][itemIndex].count -= 1;
      if (fpc[rowIndex][itemIndex].count <= 0) {
        fpc[rowIndex].splice(itemIndex, 1);
      }
      if (!fpc[rowIndex].length) {
        fpc[rowIndex].push({ name: "Product 1", count: 1 });
      }
    }

    if (rows[rowIndex]?.finalProductDetails?.length) {
      rows[rowIndex].finalProductDetails.splice(itemIndex, 1);
    }

    // Recompute base WASTAGE
    const rawUnits = rows[rowIndex]?.rProduct_name_Units || [];
    const rawSqIn = rawUnits.find((u) => u.unit === "SqIn")?.qty || 0;
    const rawKg =
      rawUnits.find((u) => u.unit === "Kg" || u.unit === "kg")?.qty || 0;

    const sumBy = (arr, unit) =>
      (arr || []).reduce((acc, p) => {
        (p.fProduct_name_Units || []).forEach((u) => {
          if (
            unit === "SqIn"
              ? u.unit === "SqIn"
              : u.unit === "Kg" || u.unit === "kg"
          ) {
            acc += Number(u.qty || 0);
          }
        });
        return acc;
      }, 0);

    const totalSq = sumBy(rows[rowIndex]?.finalProductDetails, "SqIn");
    const totalKg = sumBy(rows[rowIndex]?.finalProductDetails, "Kg");

    const wMaster = allProductName.find((p) => p.id === "WASTAGE");
    const wId = wMaster?._id || rows[rowIndex]?.rProduct_name || null;

    rows[rowIndex].wastageProductDetails = [
      {
        wProduct_name: wId,
        wProduct_name_Units: [
          {
            unit: "SqIn",
            type: "text",
            qty: Number((rawSqIn - totalSq).toFixed(3)),
            value: Number((rawSqIn - totalSq).toFixed(3)),
          },
          {
            unit: "Kg",
            type: "text",
            qty: Number((rawKg - totalKg).toFixed(3)),
            value: Number((rawKg - totalKg).toFixed(3)),
          },
        ],
      },
    ];

    setRowsData(rows);
    localStorage.setItem("rowsDataSaved", JSON.stringify(rows));

    setWastageProductCount((prev) => {
      const w = [...prev];
      w[rowIndex] = [{ name: "WASTAGE", count: 1 }];
      localStorage.setItem("wastageProductCount", JSON.stringify(w));
      return w;
    });

    const wn = [...selectedWastageName];
    if (!wn[rowIndex]) wn[rowIndex] = [];
    wn[rowIndex] = [wMaster?.id || "WASTAGE"];
    setSelectedWastageName(wn);

    setFinalProductCount(fpc);
    localStorage.setItem("finalProductCount", JSON.stringify(fpc));
  };

  const handleAddMoreWastage = (rowIndex) => {
    const wpc = [...wastageProductCount];
    if (!wpc[rowIndex]) wpc[rowIndex] = [{ name: "Wastage 1", count: 1 }];
    else {
      const nextIdx = wpc[rowIndex].length + 1;
      wpc[rowIndex].push({ name: `Wastage ${nextIdx}`, count: 1 });
    }
    setWastageProductCount(wpc);
    localStorage.setItem("wastageProductCount", JSON.stringify(wpc));
  };

  const handleRemoveWastage = (rowIndex, itemIndex) => {
    const rows = JSON.parse(localStorage.getItem("rowsDataSaved") || "[]");
    const wpc = [...wastageProductCount];

    if (!rows[rowIndex]?.wastageProductDetails) {
      if (wpc[rowIndex]) {
        wpc[rowIndex].pop();
        setWastageProductCount(wpc);
        localStorage.setItem("wastageProductCount", JSON.stringify(wpc));
      }
      return;
    }

    if (itemIndex !== 0) {
      // remove that custom wastage
      rows[rowIndex].wastageProductDetails.splice(itemIndex, 1);
      if (wpc[rowIndex]) wpc[rowIndex].splice(itemIndex, 1);
    } else {
      // removing base “WASTAGE” resets names & count
      const wn = [...selectedWastageName];
      wn[rowIndex] = (wn[rowIndex] || []).filter((_, idx) => idx !== itemIndex);
      setSelectedWastageName(wn);
      localStorage.setItem("selectedWastageName", JSON.stringify(wn));

      if (wpc[rowIndex]) wpc[rowIndex].pop();
    }

    // recompute back remaining into first slot
    const base = rows[rowIndex].wastageProductDetails?.[0];
    if (base?.wProduct_name_Units?.length) {
      // (keep as-is; remains will be updated by next unit change)
    }

    setWastageProductCount(wpc);
    localStorage.setItem("wastageProductCount", JSON.stringify(wpc));

    localStorage.setItem("rowsDataSaved", JSON.stringify(rows));
    setRowsData(rows);
  };

  // ───────────────────────────────────────────────────────────────
  // Process & step selectors + dependent loads
  // ───────────────────────────────────────────────────────────────

  const handleSelectProcessChange = (e) => {
    setSelectedStep("");
    setSelectedWorkerName([]);
    setSelecteRawMaterial([]);
    setSelectedProductName([]);
    setSelectedWastageName([]);
    setProductQuantity([]);
    setProductUnit([]);
    setRowCount(1);
    setRowsData([
      {
        user_name: "",
        rProduct_name: "",
        finalProductDetails: [],
        wastageProductDetails: [],
        rProduct_name_Units: [],
      },
    ]);

    if (e.target.value) {
      try {
        const opt = JSON.parse(e.target.value);
        setSubmitData((prev) => ({
          ...prev,
          selectedProcess: opt._id,
          selectedProcessName: opt.name,
        }));
      } catch {
        // ignore parse error
      }
    } else {
      setSubmitData((prev) => ({ ...prev, selectedProcess: null }));
    }
  };

  const handleStepNameChange = (e, index) => {
    setSelectedStepIndex(index);
    setProductionData((prev) => ({ ...prev, step_name: e.target.value }));
    const v = e.target.value || submitData.selectedStepName;
    SubRequiredProductsData(v);
  };

  const handleSelectChange = (e) => {
    setProductQuantity([]);
    setProductUnit([]);
    setRowCount(1);
    setSelectedWorkerName([]);
    setSelecteRawMaterial([]);
    setSelectedProductName([]);
    setSelectedWastageName([]);
    setRowsData([
      {
        user_name: "",
        rProduct_name: "",
        finalProductDetails: [],
        wastageProductDetails: [],
        rProduct_name_Units: [],
      },
    ]);

    const idx = e.target.selectedIndex;
    setSubmitData((prev) => ({ ...prev, selectedStepName: e.target.value }));
    setSelectedStep(e.target.value);
    handleStepNameChange(e, idx);
  };

  // Keep processSteps in sync with selected process
  useEffect(() => {
    const match = stepsData.find((d) => d._id === submitData.selectedProcess);
    setProcessSteps(match ? match.steps : []);
  }, [submitData.selectedProcess, stepsData]);

  // Load eligible workers for selected step
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    if (!userData) {
      setSelectWorker([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    CreateAccountList(userData._id, userData.database)
      .then((res) => {
        // 🔧 Show only Operators (per your ask)
        const pick = (res?.adminDetails || []).filter(
          (w) => (w?.rolename?.roleName || "").toLowerCase() === "operator"
        );
        setSelectWorker(pick);
      })
      .catch(() => setSelectWorker([]))
      .finally(() => setIsLoading(false));
  }, [submitData]);

  // Load products per step: first step uses SubStep; others include NextSubStep
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    if (!userData) {
      setProductName([]);
      return;
    }
    _GetDataBydatabaseName(userData?.database)
      .then((res) => {
        setAllProductName(res?.Product || []);
        const stepIdx = processSteps.findIndex(
          (s) => s.step_Name === submitData.selectedStepName
        );

        if (stepIdx === 0) {
          const list =
            (res?.Product || []).filter(
              (p) => p.SubStep === submitData?.selectedStepName
            ) || [];
          setProductName(list);
          setTotalProductName(res?.Product || []);
        } else {
          const list =
            (res?.Product || []).filter((p) => {
              const next = p.NextSubStep ?? "";
              return (
                next === submitData?.selectedStepName ||
                p.SubStep === submitData?.selectedStepName
              );
            }) || [];
          setProductName(list);
          setTotalProductName(res?.Product || []);
        }
      })
      .catch((e) => console.error("Error fetching products:", e));
  }, [submitData, processSteps]);

  // A small helper for row-level “target given” feasibility
  const individuallyWorkerTarget = async (index) => {
    const product = apiSubData?.daily?.product;
    if (!product) return;

    const saved = localStorage.getItem("rowsDataSaved");
    const rows = saved ? JSON.parse(saved) : [];
    if (!rows.length) return;

    // how much base (SqIn/Kg) assigned for this row
    const baseVal =
      rows[index]?.rProduct_name_Units?.find(
        (u) => u.unit === "SqIn" || u.unit === "Kg" || u.unit === "kg"
      )?.value || 0;

    const match = allProductName.find((i) => i.Product_Title === product);
    if (!match) return;

    const units = JSON.parse(JSON.stringify(match.Units || []));
    const stockUnit = match.stockUnit;

    if (units.length && !units.some((u) => u.conversionFactors)) {
      units.forEach((u) => {
        u.conversionFactors = units.map((o) => ({
          unit: o.unit,
          factor: u.qty && o.qty ? o.qty / u.qty : 0,
        }));
      });
    }

    let base = units.find((u) => u.unit === "SqIn");
    if (!base) base = units.find((u) => u.unit === "Kg" || u.unit === "kg");
    if (!base) return;

    base.qty = baseVal;
    base.value = baseVal;
    units.forEach((u) => {
      if (u.unit === base.unit) return;
      const cf = base.conversionFactors?.find((f) => f.unit === u.unit);
      u.qty = cf ? Number((baseVal * cf.factor).toFixed(3)) : 0;
      u.value = u.qty;
    });

    const stock = units.find((u) => u.unit === stockUnit)?.qty || 0;
    const wt = [...workersTarget];
    wt[index] = stock;
    setWorkersTarget(wt);
  };

  // Manual override of per-worker “target given”
  const handleWorkerTargetChangeFuntion = async (e, index) => {
    const value = parseFloat(e.target.value || 0);
    const product = apiSubData?.daily?.product;
    if (!product) return;

    const saved = localStorage.getItem("rowsDataSaved");
    const rows = saved ? JSON.parse(saved) : [];
    if (!rows.length) return;

    const rawId = rows[0]?.rProduct_name;
    const rawProd = allProductName.find((d) => d._id === rawId);
    if (!rawProd) return;

    // available raw = (assigned unit for this row) * current stock qty
    const rawUnits = rows[index]?.rProduct_name_Units || [];
    const preferred =
      rawUnits.find((u) => u.unit === "SqIn") ||
      rawUnits.find((u) => u.unit === "Kg" || u.unit === "kg");

    const rawStock = await GetUpdatedStock(
      rawProd?.warehouse?._id,
      rawProd?._id
    );
    const stockQty = Number(rawStock?.currentStock?.currentStock || 0);
    const totalBaseAvailable = Number(preferred?.qty || 0) * stockQty;

    const targetProduct = allProductName.find(
      (i) => i.Product_Title === product
    );
    if (!targetProduct) return;

    const units = JSON.parse(JSON.stringify(targetProduct.Units || []));
    if (units.length && !units.some((u) => u.conversionFactors)) {
      units.forEach((u) => {
        u.conversionFactors = units.map((o) => ({
          unit: o.unit,
          factor: u.qty && o.qty ? o.qty / u.qty : 0,
        }));
      });
    }

    // push into the first unit and cascade
    const base = units[0];
    base.qty = value;
    const cfs = base.conversionFactors || [];
    units.forEach((u) => {
      if (u.unit === base.unit) return;
      const cf = cfs.find((f) => f.unit === u.unit);
      u.qty = cf ? value * cf.factor : 0;
    });

    // Check base (SqIn -> else Kg) vs availability
    const sq = units.find((u) => u.unit === "SqIn")?.qty;
    const kg = units.find((u) => u.unit === "Kg" || u.unit === "kg")?.qty;
    const baseQty =
      typeof sq === "number" ? sq : typeof kg === "number" ? kg : 0;

    if (baseQty > totalBaseAvailable) {
      swal("Error", "Stock Unavailable", "error");
      return;
    }

    const wt = [...workersTarget];
    wt[index] = value;
    setWorkersTarget(wt);

    // Update raw material base to reflect this allocation
    const rawUnitsAgain = JSON.parse(JSON.stringify(rawProd?.Units || []));
    if (
      rawUnitsAgain.length &&
      !rawUnitsAgain.some((u) => u.conversionFactors)
    ) {
      rawUnitsAgain.forEach((u) => {
        u.conversionFactors = rawUnitsAgain.map((o) => ({
          unit: o.unit,
          factor: u.qty && o.qty ? o.qty / u.qty : 0,
        }));
      });
    }

    let baseRm =
      rawUnitsAgain.find((u) => u.unit === "SqIn") ||
      rawUnitsAgain.find((u) => u.unit === "Kg" || u.unit === "kg");
    if (!baseRm) return;

    const baseVal = baseQty;
    rawUnitsAgain.forEach((u) => {
      if (u.unit === baseRm.unit) return;
      const cf = baseRm.conversionFactors?.find((f) => f.unit === u.unit);
      u.qty = cf ? baseVal * cf.factor : 0;
    });

    const firstUpdated = rawUnitsAgain[0];
    handleUnitValueChange(
      index,
      "rProduct_name_Units",
      0,
      firstUpdated?.qty || baseVal
    );
  };

  // Totals helper used in the target table
  const totalTargetGiven = () =>
    Number(
      (workersTarget || []).reduce((acc, curr) => acc + Number(curr || 0), 0)
    );

  // Live stock hint for a raw material row (subtracts what’s already assigned on same product above)
  const updatedProductQuantity = (obj, index) => {
    if (!obj) return 0;
    const { productQuantity } = obj;
    const rows = JSON.parse(localStorage.getItem("rowsDataSaved") || "[]");
    const current = rows?.[index];
    if (!current) return productQuantity;

    const selfAssigned = Number(current?.rProduct_name_Units?.[0]?.value || 0);
    let live = productQuantity - selfAssigned;

    for (let i = 0; i < index; i++) {
      if (rows?.[i]?.rawMaterialName === current?.rawMaterialName) {
        live -= Number(rows?.[i]?.rProduct_name_Units?.[0]?.value || 0);
      }
    }

    return live < 0 ? 0 : Number(live.toFixed(3));
  };

  // Compact row UI (Worker → Raw → Units → Final → Wastage)
  const RawMaterialProcess = () => (
    <div className="flex flex-col gap-4">
      {Array.from({ length: rowCount }, (_, index) => {
        const row = rowsData?.[index] || {};
        return (
          <div
            key={index}
            className="p-2 rounded-md"
            style={{ background: "#fff", border: "1px solid #eee" }}
          >
            {/* Worker & Target Given */}
            <div className="d-flex" style={{ gap: 16, flexWrap: "wrap" }}>
              <div>
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Worker&nbsp;
                  <Link to="/app/SoftNumen/account/CreateAccount/0">+</Link>
                </Label>
                <select
                  value={selectedWorkerName[index] || ""}
                  onChange={(e) =>
                    handleWorkerChangeFuntion(
                      index,
                      e.target.value,
                      e.target.selectedOptions[0].getAttribute("data-id")
                    )
                  }
                  className="form-control"
                  style={{ minWidth: 160, fontSize: 12 }}
                >
                  <option value="">Select Worker</option>
                  {selectWorker.map((w) => (
                    <option key={w._id} data-id={w._id}>
                      {w.firstName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Target Given
                </Label>
                <Input
                  type="number"
                  value={workersTarget[index] || ""}
                  onChange={(e) => handleWorkerTargetChangeFuntion(e, index)}
                  style={{ minWidth: 160, fontSize: 12, fontWeight: 700 }}
                />
              </div>
            </div>

            {/* Raw Material */}
            <div className="d-flex mt-2" style={{ gap: 16, flexWrap: "wrap" }}>
              <div>
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Raw Material&nbsp;
                  <Link to="/app/freshlist/house/AddRawProduct/0">+</Link>
                </Label>
                <select
                  value={selectedRawMaterial[index] || ""}
                  onChange={(e) =>
                    handleRawMaterialChangeFuntion(
                      index,
                      e.target.value,
                      e.target.selectedOptions[0].getAttribute("data-id")
                    )
                  }
                  className="form-control"
                  style={{ minWidth: 160, fontSize: 12 }}
                >
                  <option value="">Select</option>
                  {productName.map((p) => (
                    <option key={p._id} value={p.id} data-id={p._id}>
                      {p.id}
                    </option>
                  ))}
                </select>
                <div
                  style={{ fontSize: 11, color: "crimson", fontWeight: 700 }}
                >
                  {selectedRawMaterial[index]
                    ? productQuantity[index]?.productQuantity > 0
                      ? `${productUnit[index]}: ${updatedProductQuantity(
                          productQuantity[index],
                          index
                        )}`
                      : ""
                    : ""}
                </div>
              </div>

              {/* Raw Units */}
              {row?.rProduct_name_Units?.length ? (
                <div
                  className="d-flex align-items-end"
                  style={{ gap: 8, flexWrap: "wrap" }}
                >
                  {row.rProduct_name_Units.map((u, ui) => (
                    <div key={ui}>
                      <Label className="mb-1" style={{ fontSize: 10 }}>
                        {u.unit}
                      </Label>
                      <Input
                        type="number"
                        value={u.value ?? u.qty ?? 0}
                        onChange={(e) =>
                          handleUnitValueChange(
                            index,
                            "rProduct_name_Units",
                            ui,
                            e.target.value
                          )
                        }
                        style={{ width: 80, fontSize: 12 }}
                      />
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            {/* Final Products (repeatable) */}
            <div className="d-flex mt-3" style={{ gap: 16, flexWrap: "wrap" }}>
              {(
                finalProductCount[index] || [{ name: "Product 1", count: 1 }]
              ).map((_, finalIdx) => {
                const fp = row?.finalProductDetails?.[finalIdx];
                return (
                  <div
                    key={finalIdx}
                    className="p-2"
                    style={{ border: "1px dashed #ddd", borderRadius: 8 }}
                  >
                    <Label className="mb-1" style={{ fontSize: 12 }}>
                      Product&nbsp;
                      <Link to="/app/freshlist/house/AddRawProduct/0">+</Link>
                    </Label>
                    <select
                      value={selectedProductName[index]?.[finalIdx] || ""}
                      onChange={(e) =>
                        handleProductNameChangeFuntion(
                          index,
                          e.target.value,
                          e.target.selectedOptions[0]?.getAttribute("data-id"),
                          finalIdx
                        )
                      }
                      className="form-control"
                      style={{ minWidth: 160, fontSize: 12 }}
                    >
                      <option value="">Select</option>
                      {(totalProductName || []).map((p) => (
                        <option key={p._id} value={p.id} data-id={p._id}>
                          {p.id}
                        </option>
                      ))}
                    </select>

                    {/* Final Units */}
                    {fp?.fProduct_name_Units?.length ? (
                      <div
                        className="d-flex mt-2"
                        style={{ gap: 8, flexWrap: "wrap" }}
                      >
                        {fp.fProduct_name_Units.map((u, ui) => (
                          <div key={ui}>
                            <Label className="mb-1" style={{ fontSize: 10 }}>
                              {u.unit}
                            </Label>
                            <Input
                              type="number"
                              value={u.qty ?? 0}
                              onChange={(e) =>
                                handleUnitValueChange(
                                  index,
                                  "fProduct_name_Units",
                                  ui,
                                  e.target.value,
                                  finalIdx
                                )
                              }
                              style={{ width: 80, fontSize: 12 }}
                            />
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {finalIdx ? (
                      <Button
                        size="sm"
                        color="link"
                        onClick={() => handleRemove(index, finalIdx)}
                        style={{ marginTop: 6 }}
                      >
                        ❌ Remove
                      </Button>
                    ) : null}
                  </div>
                );
              })}

              <Button color="link" onClick={() => handleAddMore(index)}>
                + Add More Product
              </Button>
            </div>

            {/* Wastage (repeatable; first row auto “WASTAGE”) */}
            <div className="d-flex mt-3" style={{ gap: 16, flexWrap: "wrap" }}>
              {(
                wastageProductCount[index] || [{ name: "Wastage 1", count: 1 }]
              ).map((_, wIdx) => {
                const w = row?.wastageProductDetails?.[wIdx];
                return (
                  <div
                    key={wIdx}
                    className="p-2"
                    style={{ border: "1px dashed #ddd", borderRadius: 8 }}
                  >
                    <Label className="mb-1" style={{ fontSize: 12 }}>
                      Wastage
                    </Label>
                    <select
                      value={selectedWastageName[index]?.[wIdx] || ""}
                      onChange={(e) =>
                        handleWastageChangeFuntion(
                          index,
                          e.target.value,
                          e.target.selectedOptions[0]?.getAttribute("data-id"),
                          wIdx
                        )
                      }
                      disabled={
                        selectedWastageName[index] &&
                        (selectedWastageName[index][wIdx] === "WASTAGE" ||
                          (selectedWastageName[index].length === 1 &&
                            selectedWastageName[index][0] !== "WASTAGE" &&
                            (wastageProductCount[index] || []).length === 1))
                      }
                      className="form-control"
                      style={{ minWidth: 160, fontSize: 12 }}
                    >
                      <option value="">Select</option>
                      {allProductName
                        .filter(
                          (p) =>
                            (p.id === "WASTAGE" ||
                              p.id === selectedRawMaterial[index]) &&
                            (!selectedWastageName.some((arr) =>
                              arr?.includes(p.id)
                            ) ||
                              selectedWastageName[index]?.[wIdx] === p.id)
                        )
                        .map((p) => (
                          <option key={p._id} value={p.id} data-id={p._id}>
                            {p.id}
                          </option>
                        ))}
                    </select>

                    {w?.wProduct_name_Units?.length ? (
                      <div
                        className="d-flex mt-2"
                        style={{ gap: 8, flexWrap: "wrap" }}
                      >
                        {w.wProduct_name_Units.map((u, ui) => (
                          <div key={ui}>
                            <Label className="mb-1" style={{ fontSize: 10 }}>
                              {u.unit}
                            </Label>
                            <Input
                              type="number"
                              value={u.qty <= 0 ? 0 : u.qty}
                              onChange={(e) =>
                                handleUnitValueChange(
                                  index,
                                  "wProduct_name_Units",
                                  ui,
                                  e.target.value,
                                  wIdx
                                )
                              }
                              disabled={
                                selectedWastageName[index] &&
                                (selectedWastageName[index][wIdx] ===
                                  "WASTAGE" ||
                                  (selectedWastageName[index].length === 1 &&
                                    selectedWastageName[index][0] !==
                                      "WASTAGE"))
                              }
                              style={{ width: 80, fontSize: 12 }}
                            />
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {wIdx ? (
                      <Button
                        size="sm"
                        color="link"
                        onClick={() => handleRemoveWastage(index, wIdx)}
                        style={{ marginTop: 6 }}
                      >
                        ❌ Remove
                      </Button>
                    ) : null}
                  </div>
                );
              })}

              {(row?.wastageProductDetails?.length || 0) >= 2 ? null : (
                <Button
                  color="link"
                  onClick={() => handleAddMoreWastage(index)}
                >
                  + Add More Wastage
                </Button>
              )}

              {index ? (
                <Button
                  size="sm"
                  color="link"
                  onClick={() => handleRemoveRow(index)}
                  style={{ alignSelf: "center" }}
                >
                  ❌ Remove Row
                </Button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );

  // Submit payload -> API, then clear + navigate
  const handleSubmitProduction = async () => {
    // basic guards
    if (!submitData.selectedProcess || !submitData.selectedStepName) {
      swal("Error!", "Please select process and step.", "error");
      return;
    }

    const rows = JSON.parse(localStorage.getItem("rowsDataSaved") || "[]");
    if (!rows?.length) {
      swal("Error!", "Please add at least one row.", "error");
      return;
    }

    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const totalSteps = processSteps?.length || 0;

    // 🔧 ensure each wastage line has a valid wProduct_name
    const wastageMaster = allProductName.find((p) => p.id === "WASTAGE");
    const wastageId = wastageMaster?._id || null;

    const safeRows = rows.map((r) => {
      const ensuredW = (r.wastageProductDetails || []).map((w) => ({
        ...w,
        wProduct_name:
          w?.wProduct_name || wastageId || r?.rProduct_name || null,
      }));
      return { ...r, wastageProductDetails: ensuredW };
    });

    const payload = {
      database: userData?.database || productionData.database,
      processName: submitData.selectedProcess,
      step_name: submitData.selectedStepName,
      totalStep: totalSteps,
      currentStep: selectedStepIndex,
      product_details: safeRows,
    };

    try {
      const res = await StartProductionApi(payload);
      if (res?.status) {
        swal("Success!", "Submitted Successfully!", "success");
        clearfilled();
        navigate("/apps/freshlist/Production/DailyActivityReport");
      } else {
        swal("Error!", "Failed to submit.", "error");
      }
    } catch (e) {
      swal("Error!", e?.message || "Something went wrong.", "error");
    }
  };

  // Loading state
  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: "60vh" }}
      >
        <Spinner />
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────

  return (
    <div>
      <Card>
        <CardBody>
          <Row className="justify-content-between align-items-center">
            <Col xs="12" lg="6" className="mb-2">
              <h1 className="h3 m-0">Start Production</h1>
            </Col>
            <Col xs="12" lg="6" className="text-lg-right text-center">
              <Link to="/apps/freshlist/Production/DailyActivityReport">
                <Button color="primary">Daily Production Report</Button>
              </Link>
            </Col>
          </Row>

          {/* Target product picker (drives apiData/apiSubData) */}
          <Row className="mt-3">
            <Col sm="4">
              <Label className="mb-1">Select Target Product</Label>
              <select
                onChange={(e) =>
                  handleSelectFinalChange(
                    e.target.value,
                    e.target.selectedOptions[0].getAttribute("id")
                  )
                }
                value={selectedFinalProduct || ""}
                className="form-control"
              >
                <option value="" disabled>
                  Select Target Product
                </option>
                {targets.map((t, i) => (
                  <option key={i} id={t.productId} value={t.product}>
                    {t.product}
                  </option>
                ))}
              </select>
            </Col>
          </Row>

          {/* Overall target snapshot (apiData) */}
          {selectedFinalProduct && apiData ? (
            <Row className="mt-3">
              <Col sm="12">
                <Table striped bordered responsive size="sm">
                  <thead>
                    <tr>
                      {[
                        "Daily",
                        "Monthly",
                        "3 Months",
                        "6 Months",
                        "Yearly",
                      ].map((h) => (
                        <th key={h} className="text-center">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {Object.keys(apiData).map((key) => {
                        const d = apiData[key];
                        const done =
                          Number(d?.AchievedTargetPrimary || 0) >=
                          Number(d?.dailyTargetPrimary || 0);
                        return (
                          <td key={key}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600 }}>
                                {d.product}
                              </div>
                              <div className="d-flex" style={{ gap: 8 }}>
                                <div className="w-50">
                                  <small>Target ({d.unit})</small>
                                  <Input
                                    readOnly
                                    value={d?.dailyTargetPrimary || 0}
                                  />
                                </div>
                                <div className="w-50">
                                  <small>Achieved ({d.unit})</small>
                                  <Input
                                    readOnly
                                    value={d?.AchievedTargetPrimary || 0}
                                  />
                                </div>
                              </div>
                              {done ? (
                                <div
                                  className="text-success text-center"
                                  style={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  Target Completed
                                </div>
                              ) : null}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
          ) : null}

          {/* Process / Step */}
          <Row className="mt-3">
            <Col md="4" className="mb-2">
              <Label className="mb-1">
                Select Process&nbsp;
                <Link to="/app/freshlist/category/addCategory">+</Link>
              </Label>
              <Input
                type="select"
                value={JSON.stringify({
                  name: submitData.selectedProcessName || "",
                  _id: submitData.selectedProcess || "",
                })}
                onChange={handleSelectProcessChange}
              >
                <option value="">Select Process</option>
                {stepsData.map((p) => (
                  <option
                    key={p._id}
                    value={JSON.stringify({ name: p.name, _id: p._id })}
                  >
                    {p.name}
                  </option>
                ))}
              </Input>
            </Col>

            <Col md="4" className="mb-2">
              <Label className="mb-1">
                Step Name&nbsp;
                <Link to="/views/apps/freshlist/Production/productionprocesspage">
                  +
                </Link>
              </Label>
              <Input
                type="select"
                value={submitData.selectedStepName || ""}
                onChange={handleSelectChange}
              >
                <option value="">Select Step</option>
                {processSteps?.map((s, i) => (
                  <option key={i}>{s.step_Name}</option>
                ))}
              </Input>
            </Col>
          </Row>

          {/* Sub-required product target snapshot (apiSubData) */}
          {submitData.selectedStepName && apiSubData ? (
            <Row className="mt-3">
              <Col sm="12">
                <Table striped bordered responsive size="sm">
                  <thead>
                    <tr>
                      <th className="text-center">Product</th>
                      {[
                        "Daily",
                        "Monthly",
                        "3 Months",
                        "6 Months",
                        "Yearly",
                      ].map((h) => (
                        <th key={h} className="text-center">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td
                        className="align-middle text-center"
                        style={{ fontWeight: 600 }}
                      >
                        {apiSubData?.daily?.product}
                      </td>
                      {[
                        {
                          target: apiSubData.daily.dailyTarget,
                          achieved:
                            apiSubData.daily.AchievedTarget + realTimeUpdate,
                          unit: apiSubData.daily.unit,
                        },
                        {
                          target: apiSubData.monthly.monthlyTarget,
                          achieved:
                            apiSubData.monthly.AchievedTarget + realTimeUpdate,
                          unit: apiSubData.monthly.unit,
                        },
                        {
                          target: apiSubData.threeMonths.threeMonthsTarget,
                          achieved:
                            apiSubData.threeMonths.AchievedTarget +
                            realTimeUpdate,
                          unit: apiSubData.threeMonths.unit,
                        },
                        {
                          target: apiSubData.sixMonths.sixMonthsTarget,
                          achieved:
                            apiSubData.sixMonths.AchievedTarget +
                            realTimeUpdate,
                          unit: apiSubData.sixMonths.unit,
                        },
                        {
                          target: apiSubData.yearly.yearlyTarget,
                          achieved:
                            apiSubData.yearly.AchievedTarget + realTimeUpdate,
                          unit: apiSubData.yearly.unit,
                        },
                      ].map((c, i) => {
                        const remaining =
                          Number(c.target || 0) - Number(totalTargetGiven());
                        const completed = remaining <= 0;
                        return (
                          <td key={i}>
                            <div className="d-flex" style={{ gap: 8 }}>
                              <div className="w-33">
                                <small>Target ({c.unit})</small>
                                <Input readOnly value={c.target || 0} />
                              </div>
                              <div className="w-33">
                                <small>Target Given</small>
                                <Input readOnly value={totalTargetGiven()} />
                              </div>
                              <div className="w-33">
                                <small>Remaining</small>
                                <Input
                                  readOnly
                                  value={
                                    completed
                                      ? "Completed"
                                      : Math.max(0, remaining)
                                  }
                                  style={{
                                    color: completed ? "green" : undefined,
                                    fontWeight: 700,
                                  }}
                                />
                              </div>
                            </div>
                            <div className="mt-1">
                              <small>Achieved</small>
                              <Input readOnly value={Number(c.achieved || 0)} />
                              {Number(c.target || 0) <=
                              Number(c.achieved || 0) ? (
                                <div
                                  className="text-success"
                                  style={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  Target completed
                                </div>
                              ) : null}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
          ) : null}

          {/* Main rows UI */}
          <div className="mt-3">{RawMaterialProcess()}</div>

          {/* Actions */}
          <Row className="mt-3">
            <Col className="text-center">
              <Button color="primary" onClick={handleSubmitProduction}>
                Submit
              </Button>
            </Col>
            <Col className="text-center">
              <Button color="secondary" onClick={handleIncreaseRow}>
                + Add Row
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </div>
  );
}

export default StartProduction;
